import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, BadgeCheck, CircleSlash, Pencil, Plus, Reply, Send, Trash2, UserPlus } from 'lucide-react';
import {
  DEPARTMENT_META,
  LIMITS,
  TASK_PRIORITIES,
  TASK_PRIORITY_META,
  formatTicketReference,
  type TicketAvailableAction,
  type TicketComment,
} from '@ssn/shared';
import {
  useAddTicketComment,
  useAssignTicket,
  useCompleteTicket,
  useCreateTaskFromTicket,
  useDeleteTicketComment,
  useDenyTicket,
  useEditTicketComment,
  useReferenceData,
  useTicket,
} from '@/lib/queries';
import { useSession } from '@/session/SessionProvider';
import { Panel } from '@/components/ui/Panel';
import { Button, IconButton } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select, TextArea, TextInput } from '@/components/ui/Form';
import { Callout, EmptyState, ErrorState, InlineError, LoadingBlock } from '@/components/ui/Feedback';
import { Avatar, UserChip } from '@/components/ui/Avatar';
import { DepartmentBadge, TicketStatusBadge } from '@/components/domain/ticket-status';
import { StatusBadge, PriorityBadge } from '@/components/domain/status';
import { formatDateTime, formatRelativeTime } from '@/lib/format';

const INTENT_VARIANT = { primary: 'primary', positive: 'positive', danger: 'danger' } as const;

export function TicketDetailPage() {
  const { reference: referenceParam } = useParams<{ reference: string }>();
  const reference = Number(referenceParam);
  const { session } = useSession();

  const [assigning, setAssigning] = useState(false);
  const [pendingAction, setPendingAction] = useState<TicketAvailableAction | null>(null);
  const [creatingTask, setCreatingTask] = useState(false);

  const { data, isPending, error, refetch } = useTicket(reference, { enabled: Number.isFinite(reference) });
  const complete = useCompleteTicket(Number.isFinite(reference) ? reference : 0);

  if (!Number.isFinite(reference)) return <ErrorState error={new Error('That is not a valid request number.')} />;
  if (isPending) return <LoadingBlock label="Loading request" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data || !session) return null;

  const ticket = data.ticket;
  const permissions = ticket.viewerPermissions;

  // Every action either needs a reason (DENY, routed to a modal), needs a
  // developer picked (ASSIGN, routed to its own modal), or — the only
  // remaining case — is COMPLETE, which just runs.
  const runAction = (action: TicketAvailableAction): void => {
    if (action.requiresComment) {
      setPendingAction(action);
    } else if (action.action === 'ASSIGN') {
      setAssigning(true);
    } else {
      complete.mutate(null);
    }
  };

  return (
    <>
      <header className="mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-xs text-text-subtle">{formatTicketReference(ticket.reference)}</p>
            <h1 className="mt-0.5 text-xl leading-snug font-semibold text-text">{ticket.title}</h1>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            <DepartmentBadge department={ticket.department} />
            <TicketStatusBadge status={ticket.status} />
            {permissions.canAssign && (
              <IconButton label={ticket.assignee ? 'Reassign this request' : 'Assign this request'} onClick={() => setAssigning(true)}>
                <UserPlus aria-hidden className="size-4" />
              </IconButton>
            )}
          </div>
        </div>
      </header>

      {ticket.status === 'DENIED' && (
        <Callout tone="critical" className="mb-5" icon={<CircleSlash aria-hidden className="size-3.5" />} title="This request was denied">
          {ticket.deniedBy && <>Closed by {ticket.deniedBy.displayName}. </>}
          {ticket.deniedReason}
        </Callout>
      )}
      {ticket.status === 'COMPLETED' && (
        <Callout tone="success" className="mb-5" icon={<BadgeCheck aria-hidden className="size-3.5" />} title="This request is complete">
          {ticket.completedBy && <>Signed off by {ticket.completedBy.displayName}.</>}
        </Callout>
      )}

      {ticket.availableActions.length > 0 && (
        <Panel className="mb-5" bodyClassName="py-3">
          <div className="flex flex-wrap items-center gap-2">
            {ticket.availableActions.map((action) => (
              <Button
                key={action.action}
                variant={INTENT_VARIANT[action.intent]}
                loading={action.action === 'COMPLETE' && complete.isPending}
                onClick={() => runAction(action)}
              >
                {action.label}
              </Button>
            ))}
            {permissions.canCreateTask && (
              <Button variant="secondary" onClick={() => setCreatingTask(true)} icon={<Plus aria-hidden className="size-4" />}>
                Create Task
              </Button>
            )}
          </div>
          <InlineError error={complete.error} className="mt-2.5" />
        </Panel>
      )}
      {permissions.canCreateTask && ticket.availableActions.length === 0 && (
        <Panel className="mb-5" bodyClassName="py-3">
          <Button variant="secondary" onClick={() => setCreatingTask(true)} icon={<Plus aria-hidden className="size-4" />}>
            Create Task
          </Button>
        </Panel>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Panel title="Description">
            {ticket.description.trim() ? (
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-text">{ticket.description}</p>
            ) : (
              <p className="text-sm text-text-subtle italic">No description was provided.</p>
            )}
          </Panel>

          <Panel title="Linked tasks" description={`${ticket.linkedTasks.length} task(s) created from this request.`}>
            {ticket.linkedTasks.length === 0 ? (
              <p className="text-sm text-text-subtle">
                {permissions.canCreateTask
                  ? 'No tasks yet — use Create Task above to start one.'
                  : 'No tasks have been created from this request yet.'}
              </p>
            ) : (
              <ul className="space-y-2">
                {ticket.linkedTasks.map((task) => (
                  <li key={task.id} className="flex items-center gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-text">{task.title}</span>
                    <PriorityBadge priority={task.priority} size="sm" />
                    <StatusBadge status={task.status} size="sm" />
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Conversation" flush bodyClassName="p-4">
            <TicketConversation ticket={ticket} currentUserId={session.user.id} canComment={permissions.canComment} />
          </Panel>

          <Panel title="Activity">
            <TicketActivityList activity={ticket.activity} />
          </Panel>
        </div>

        <div className="space-y-5">
          <Panel title="Request information">
            <dl className="space-y-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <dt className="text-xs text-text-subtle">Opened by</dt>
                <dd>
                  <UserChip user={ticket.createdBy} size="xs" />
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-xs text-text-subtle">Assigned developer</dt>
                <dd>
                  <UserChip user={ticket.assignee} size="xs" />
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-xs text-text-subtle">Department</dt>
                <dd>
                  <DepartmentBadge department={ticket.department} size="sm" />
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-xs text-text-subtle">Opened</dt>
                <dd className="text-text" title={ticket.createdAt}>
                  {formatRelativeTime(ticket.createdAt)}
                </dd>
              </div>
              {ticket.assignedAt && (
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-xs text-text-subtle">Assigned</dt>
                  <dd className="text-text" title={ticket.assignedAt}>
                    {formatRelativeTime(ticket.assignedAt)}
                  </dd>
                </div>
              )}
              {ticket.resolvedAt && (
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-xs text-text-subtle">Resolved</dt>
                  <dd className="text-text" title={ticket.resolvedAt}>
                    {formatRelativeTime(ticket.resolvedAt)}
                  </dd>
                </div>
              )}
            </dl>
          </Panel>
        </div>
      </div>

      <AssignModal ticket={ticket} open={assigning} onClose={() => setAssigning(false)} />
      <ActionCommentModal action={pendingAction} reference={ticket.reference} onClose={() => setPendingAction(null)} />
      <CreateTaskFromTicketModal
        ticket={ticket}
        open={creatingTask}
        onClose={() => setCreatingTask(false)}
      />
    </>
  );
}

function AssignModal({
  ticket,
  open,
  onClose,
}: {
  ticket: { reference: number; assignee: { id: string } | null };
  open: boolean;
  onClose: () => void;
}) {
  const reference = useReferenceData(open);
  const assign = useAssignTicket(ticket.reference);
  const [assigneeId, setAssigneeId] = useState(ticket.assignee?.id ?? '');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={ticket.assignee ? 'Reassign this request' : 'Assign this request'}
      description="The developer is notified on Discord. Everyone else loses access to this request."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            form="assign-ticket-form"
            type="submit"
            variant="primary"
            loading={assign.isPending}
            disabled={!assigneeId || assigneeId === ticket.assignee?.id}
          >
            {ticket.assignee ? 'Reassign' : 'Assign'}
          </Button>
        </>
      }
    >
      <form
        id="assign-ticket-form"
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          assign.mutate(assigneeId, { onSuccess: onClose });
        }}
      >
        <Select
          label="Developer"
          required
          value={assigneeId}
          onChange={(event) => setAssigneeId(event.target.value)}
          placeholder="Choose a developer"
          options={(reference.data?.assignableUsers ?? [])
            .filter((user) => user.appRole !== 'DEPARTMENT_LEAD')
            .map((user) => ({ value: user.id, label: user.displayName }))}
        />
        <InlineError error={assign.error} />
      </form>
    </Modal>
  );
}

function ActionCommentModal({
  action,
  reference,
  onClose,
}: {
  action: TicketAvailableAction | null;
  reference: number;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const deny = useDenyTicket(reference);

  const close = () => {
    setReason('');
    deny.reset();
    onClose();
  };

  return (
    <Modal
      open={action !== null}
      onClose={close}
      title={action?.label ?? ''}
      description={action?.description}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            form="ticket-action-comment-form"
            type="submit"
            variant="danger"
            loading={deny.isPending}
            disabled={reason.trim().length < LIMITS.reasonMin}
          >
            {action?.label}
          </Button>
        </>
      }
    >
      <form
        id="ticket-action-comment-form"
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          deny.mutate(reason.trim(), { onSuccess: close });
        }}
      >
        <Callout tone="warning" icon={<AlertTriangle aria-hidden className="size-3.5" />}>
          The department lead who opened this request will be notified on Discord.
        </Callout>
        <TextArea
          label="Reason"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Explain why this request is being closed…"
        />
        <InlineError error={deny.error} />
      </form>
    </Modal>
  );
}

function CreateTaskFromTicketModal({
  ticket,
  open,
  onClose,
}: {
  ticket: { reference: number; assignee: { id: string } | null };
  open: boolean;
  onClose: () => void;
}) {
  const reference = useReferenceData(open);
  const createTask = useCreateTaskFromTicket(ticket.reference);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<(typeof TASK_PRIORITIES)[number]>('MEDIUM');
  const [assigneeId, setAssigneeId] = useState(ticket.assignee?.id ?? '');

  const close = () => {
    setTitle('');
    setDescription('');
    setPriority('MEDIUM');
    createTask.reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Create a task from this request"
      description="The same options as the main Create Task page. The task stays linked to this request."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            form="create-ticket-task-form"
            type="submit"
            variant="primary"
            loading={createTask.isPending}
            disabled={title.trim().length < LIMITS.titleMin || !assigneeId}
          >
            Create Task
          </Button>
        </>
      }
    >
      <form
        id="create-ticket-task-form"
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          createTask.mutate(
            { title: title.trim(), description: description.trim(), priority, assigneeId },
            { onSuccess: close },
          );
        }}
      >
        <TextInput label="Title" required autoFocus value={title} onChange={(event) => setTitle(event.target.value)} />
        <TextArea
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          className="min-h-[120px]"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Priority"
            value={priority}
            onChange={(event) => setPriority(event.target.value as typeof priority)}
            options={TASK_PRIORITIES.map((value) => ({ value, label: TASK_PRIORITY_META[value].label }))}
          />
          <Select
            label="Assign to"
            required
            value={assigneeId}
            onChange={(event) => setAssigneeId(event.target.value)}
            placeholder="Choose a developer"
            options={(reference.data?.assignableUsers ?? [])
              .filter((user) => user.appRole !== 'DEPARTMENT_LEAD')
              .map((user) => ({ value: user.id, label: user.displayName }))}
          />
        </div>
        <InlineError error={createTask.error} />
      </form>
    </Modal>
  );
}

/* ---- Conversation ---------------------------------------------------- */

function TicketConversation({
  ticket,
  currentUserId,
  canComment,
}: {
  ticket: { reference: number; comments: readonly TicketComment[] };
  currentUserId: string;
  canComment: boolean;
}) {
  const [replyTo, setReplyTo] = useState<string | null>(null);

  if (ticket.comments.length === 0 && !canComment) {
    return <EmptyState title="No messages yet" />;
  }

  return (
    <div className="space-y-4">
      {ticket.comments.length === 0 ? (
        <p className="text-sm text-text-subtle">No messages yet — start the conversation below.</p>
      ) : (
        <ol className="space-y-4">
          {ticket.comments.map((comment) => (
            <li key={comment.id}>
              <TicketCommentItem
                comment={comment}
                reference={ticket.reference}
                currentUserId={currentUserId}
                canComment={canComment}
                onReply={() => setReplyTo(comment.id)}
              />
              {comment.replies && comment.replies.length > 0 && (
                <ol className="mt-3 space-y-3 border-l-2 border-border pl-4 sm:ml-4">
                  {comment.replies.map((reply) => (
                    <li key={reply.id}>
                      <TicketCommentItem
                        comment={reply}
                        reference={ticket.reference}
                        currentUserId={currentUserId}
                        canComment={false}
                        isReply
                      />
                    </li>
                  ))}
                </ol>
              )}
              {replyTo === comment.id && canComment && (
                <div className="mt-3 sm:ml-4">
                  <TicketCommentComposer
                    reference={ticket.reference}
                    parentId={comment.id}
                    placeholder={`Reply to ${comment.author.displayName}…`}
                    onDone={() => setReplyTo(null)}
                    onCancel={() => setReplyTo(null)}
                  />
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {canComment && replyTo === null && (
        <div className="border-t border-border pt-4">
          <TicketCommentComposer reference={ticket.reference} parentId={null} placeholder="Write a message…" />
        </div>
      )}
    </div>
  );
}

function TicketCommentItem({
  comment,
  reference,
  currentUserId,
  canComment,
  isReply = false,
  onReply,
}: {
  comment: TicketComment;
  reference: number;
  currentUserId: string;
  canComment: boolean;
  isReply?: boolean;
  onReply?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const editComment = useEditTicketComment(reference);
  const deleteComment = useDeleteTicketComment(reference);

  const isAuthor = comment.author.id === currentUserId;
  const isDeleted = comment.deletedAt !== null;

  return (
    <article className={`rounded-[--radius-panel] border border-border bg-surface p-3 ${isDeleted ? 'opacity-70' : ''} ${isReply ? 'bg-surface-sunken' : ''}`}>
      <header className="flex flex-wrap items-center gap-2">
        <Avatar user={comment.author} size="sm" />
        <span className="text-sm font-medium text-text">{comment.author.displayName}</span>
        <time dateTime={comment.createdAt} title={formatDateTime(comment.createdAt)} className="text-[11px] text-text-subtle">
          {formatRelativeTime(comment.createdAt)}
        </time>
        {comment.editedAt && <span className="text-[11px] text-text-subtle">· edited</span>}

        <span className="ml-auto flex items-center gap-0.5">
          {!isDeleted && canComment && onReply && (
            <IconButton label="Reply" onClick={onReply}>
              <Reply aria-hidden className="size-3.5" />
            </IconButton>
          )}
          {!isDeleted && isAuthor && (
            <IconButton label="Edit" onClick={() => setEditing((value) => !value)}>
              <Pencil aria-hidden className="size-3.5" />
            </IconButton>
          )}
          {!isDeleted && isAuthor && (
            <IconButton
              label="Delete"
              disabled={deleteComment.isPending}
              onClick={() => {
                if (window.confirm('Delete this message?')) deleteComment.mutate(comment.id);
              }}
            >
              <Trash2 aria-hidden className="size-3.5" />
            </IconButton>
          )}
        </span>
      </header>

      {editing ? (
        <form
          className="mt-2.5 space-y-2"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            editComment.mutate({ commentId: comment.id, body: draft }, { onSuccess: () => setEditing(false) });
          }}
        >
          <TextArea value={draft} onChange={(event) => setDraft(event.target.value)} maxLength={LIMITS.commentMax} required />
          <InlineError error={editComment.error} />
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" variant="primary" loading={editComment.isPending}>
              Save
            </Button>
          </div>
        </form>
      ) : (
        <p className={`mt-2 text-sm leading-relaxed whitespace-pre-wrap ${isDeleted ? 'text-text-subtle italic' : 'text-text'}`}>
          {comment.body}
        </p>
      )}

      <InlineError error={deleteComment.error} className="mt-2" />
    </article>
  );
}

function TicketCommentComposer({
  reference,
  parentId,
  placeholder,
  onDone,
  onCancel,
}: {
  reference: number;
  parentId: string | null;
  placeholder: string;
  onDone?: () => void;
  onCancel?: () => void;
}) {
  const [body, setBody] = useState('');
  const addComment = useAddTicketComment(reference);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    addComment.mutate(
      { body: trimmed, parentId },
      {
        onSuccess: () => {
          setBody('');
          onDone?.();
        },
      },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <TextArea value={body} onChange={(event) => setBody(event.target.value)} placeholder={placeholder} maxLength={LIMITS.commentMax} />
      <InlineError error={addComment.error} />
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" size="sm" variant="primary" loading={addComment.isPending} disabled={!body.trim()} icon={<Send aria-hidden className="size-3.5" />}>
          Post
        </Button>
      </div>
    </form>
  );
}

/* ---- Activity ---------------------------------------------------------- */

function TicketActivityList({ activity }: { activity: readonly { id: string; summary: string; detail: string | null; createdAt: string }[] }) {
  if (activity.length === 0) return <EmptyState title="No activity yet" />;

  return (
    <ol className="space-y-3">
      {activity.map((entry) => (
        <li key={entry.id} className="flex gap-2.5 text-sm">
          <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-border-strong" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <p className="text-text">{entry.summary}</p>
              <time dateTime={entry.createdAt} title={formatDateTime(entry.createdAt)} className="text-[11px] text-text-subtle">
                {formatRelativeTime(entry.createdAt)}
              </time>
            </div>
            {entry.detail && (
              <p className="mt-1 rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-xs whitespace-pre-wrap text-text-muted">
                {entry.detail}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
